import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
  StatPill,
  toast,
} from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cpu, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { listBuildings } from '../../../api/buildings.js';
import {
  archiveDevice,
  listDevices,
  type DeviceWithDeviceType,
} from '../../../api/compliance.js';

import { CreateDeviceSheet } from './create-device-sheet.js';
import { DeviceDocumentsSheet } from './device-documents-sheet.js';
import { cadenceLabel, formatDate } from './lib.js';

export function ComplianceDevicesPage() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [buildingId, setBuildingId] = useState<string>('');
  const [openCreate, setOpenCreate] = useState(false);
  const [editDevice, setEditDevice] = useState<DeviceWithDeviceType | null>(null);
  const [docsDevice, setDocsDevice] = useState<DeviceWithDeviceType | null>(null);

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'all'],
    queryFn: () => listBuildings({ limit: 50 }),
  });

  const filters = useMemo(
    () => ({
      q: q || undefined,
      buildingId: buildingId || undefined,
    }),
    [q, buildingId],
  );
  const devQuery = useQuery({
    queryKey: ['compliance', 'devices', filters],
    queryFn: () => listDevices(filters),
    placeholderData: (prev) => prev,
  });

  async function handleDelete(d: DeviceWithDeviceType) {
    if (
      !confirm(
        `Delete ${d.label}? This removes the device and any open compliance items. Past logs are kept.`,
      )
    ) {
      return;
    }
    try {
      await archiveDevice(d.id);
      toast.success('Device deleted.');
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete device');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
            <Input
              placeholder="Search devices…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-72 pl-8"
            />
          </div>
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            <option value="">All buildings</option>
            {buildingsQuery.data?.buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <Plus className="h-4 w-4" />
          New device
        </Button>
      </div>

      {devQuery.isError ? (
        <ErrorState description="Could not load devices." />
      ) : devQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (devQuery.data?.devices.length ?? 0) === 0 ? (
        <EmptyState
          icon={Cpu}
          title="No devices yet"
          description="Add elevators, boilers, fire systems, and other tracked equipment to start scheduling compliance."
          action={
            <Button size="sm" onClick={() => setOpenCreate(true)}>
              <Plus className="h-4 w-4" />
              Add the first device
            </Button>
          }
        />
      ) : (
        <DataTable<DeviceWithDeviceType>
          rowKey={(r) => r.id}
          rows={devQuery.data!.devices}
          columns={[
            {
              id: 'label',
              header: 'Device',
              description: 'The equipment name, its type, and serial number.',
              cell: (r) => (
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.label}</div>
                  <div className="truncate text-xs text-fg-muted">
                    {r.deviceType.name}
                    {r.serialNumber ? ` · SN ${r.serialNumber}` : ''}
                  </div>
                </div>
              ),
            },
            {
              id: 'cadence',
              header: 'Cadence',
              description: 'How often this device must be inspected or serviced.',
              cell: (r) => (
                <span className="text-sm">
                  {cadenceLabel(
                    r.cadenceOverride ?? r.deviceType.defaultCadence,
                    r.customIntervalDaysOverride ?? r.deviceType.defaultCustomIntervalDays,
                  )}
                </span>
              ),
            },
            {
              id: 'nextDue',
              header: 'Next due',
              description: 'The next date this device needs a compliance record.',
              cell: (r) =>
                r.nextDueAt ? (
                  <span className="tabular-nums">{formatDate(r.nextDueAt)}</span>
                ) : (
                  <StatPill value="—" tone="neutral" />
                ),
            },
            {
              id: 'lastLogged',
              header: 'Last logged',
              description: 'When a compliance record was last saved for this device.',
              cell: (r) =>
                r.lastLoggedAt ? (
                  <span className="tabular-nums text-fg-secondary">
                    {formatDate(r.lastLoggedAt)}
                  </span>
                ) : (
                  <span className="text-fg-muted">Never</span>
                ),
            },
            {
              id: 'actions',
              header: '',
              cell: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditDevice(r)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDocsDevice(r)}>
                    Documents
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(r)}
                    className="text-danger hover:text-danger"
                  >
                    Delete
                  </Button>
                </div>
              ),
              className: 'text-right',
            },
          ]}
        />
      )}

      {openCreate ? (
        <CreateDeviceSheet
          onClose={() => setOpenCreate(false)}
          onSaved={() => {
            setOpenCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}

      {editDevice ? (
        <CreateDeviceSheet
          device={editDevice}
          onClose={() => setEditDevice(null)}
          onSaved={() => {
            setEditDevice(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}

      {docsDevice ? (
        <DeviceDocumentsSheet device={docsDevice} onClose={() => setDocsDevice(null)} />
      ) : null}
    </div>
  );
}
